class BundleOffer extends HTMLElement {
  connectedCallback() {
    this.current = JSON.parse(this.querySelector('[data-current]').textContent);
    this.pool = JSON.parse(this.querySelector('[data-pool]').textContent);
    this.tier = 'single';
    this.rate = Number(this.dataset.rate || 20);
    this.picks = [];
    this.draft = [];
    this.dialog = this.querySelector('[data-dialog]');
    this.querySelectorAll('[data-tier]').forEach((button) => {
      button.addEventListener('click', () => {
        this.tier = button.dataset.tier;
        this.picks = [];
        this.#render();
      });
    });
    this.querySelector('[data-search]').addEventListener('input', () => this.#results());
    this.querySelector('[data-confirm]').addEventListener('click', () => {
      this.picks = [...this.draft];
      this.dialog.close();
      this.#render();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
      const choice = event.target.closest('[data-product]');
      if (!choice) return;
      this.#toggle(Number(choice.dataset.product));
    });
    this.addEventListener('click', (event) => {
      const open = event.target.closest('[data-open]');
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        this.#open();
        return;
      }
      const clear = event.target.closest('[data-clear]');
      if (clear) {
        event.preventDefault();
        event.stopPropagation();
        this.picks = this.picks.filter((id) => id !== Number(clear.dataset.clear));
        this.#render();
      }
    });
    this.#bindCart();
    const details = this.closest('.product-details');
    if (details) {
      new MutationObserver(() => {
        this.#bindCart();
        this.#label();
      }).observe(details, { childList: true, subtree: true });
    }
    const idInput = this.#idInput();
    idInput?.addEventListener('change', () => this.#render());
    this.#render();
  }

  #idInput() {
    return this.closest('.product-details')?.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
  }

  #cartButton() {
    return this.closest('.product-details')?.querySelector('.product-form-buttons .add-to-cart-button');
  }

  #variant() {
    const id = Number(this.#idInput()?.value);
    return this.current.variants.find((variant) => variant.id === id) || this.current.variants[0];
  }

  #need() {
    if (this.tier === 'duo') return 1;
    if (this.tier === 'set') return 4;
    return 0;
  }

  #product(id) {
    return this.pool.find((product) => product.id === id) || null;
  }

  #chosen() {
    return this.picks.map((id) => this.#product(id)).filter(Boolean);
  }

  #complete() {
    return this.#chosen().length === this.#need();
  }

  #offerReady() {
    return this.tier !== 'single' && this.#complete();
  }

  #money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  #quote() {
    if (!this.#complete()) return null;
    const lines = [this.#variant().price, ...this.#chosen().map((product) => product.price)];
    const compare = lines.reduce((sum, price) => sum + price, 0);
    if (this.tier === 'set') {
      const free = [...lines].sort((a, b) => a - b).slice(0, 2).reduce((sum, price) => sum + price, 0);
      return { compare, pay: compare - free };
    }
    return { compare, pay: Math.round(compare * (100 - this.rate) / 100) };
  }

  #bindCart() {
    if (this.dataset.bound) return;
    this.dataset.bound = 'true';
    document.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button') : null;
      if (!button || button !== this.#cartButton()) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.#complete()) this.#add();
      else this.#open();
    }, true);
    document.addEventListener('submit', (event) => {
      if (event.target !== this.#cartForm()) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  #cartForm() {
    return this.closest('.product-details')?.querySelector('form[data-type="add-to-cart-form"]');
  }

  #label() {
    const button = this.#cartButton();
    if (!button) return;
    const offer = this.#offerReady();
    const label = offer ? 'Add offer to cart' : 'Add to cart';
    let labeled = false;
    button.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        node.textContent = label;
        labeled = true;
      }
    });
    if (!labeled) button.append(document.createTextNode(label));
    button.disabled = this.tier !== 'single' && !this.#complete();
  }

  #open() {
    this.draft = [...this.picks];
    const need = this.#need();
    this.querySelector('[data-dialog-title]').textContent = need === 1 ? 'Choose the second watch' : 'Choose 4 watches';
    this.querySelector('[data-search]').value = '';
    this.#results();
    this.dialog.showModal();
  }

  #toggle(id) {
    const need = this.#need();
    if (this.draft.includes(id)) this.draft = this.draft.filter((pick) => pick !== id);
    else if (need === 1) this.draft = [id];
    else if (this.draft.length < need) this.draft = [...this.draft, id];
    this.#results();
  }

  #results() {
    const query = this.querySelector('[data-search]').value.trim().toLowerCase();
    const list = this.pool.filter((product) => product.title.toLowerCase().includes(query));
    const need = this.#need();
    this.querySelector('[data-results]').innerHTML = list.map((item) => `
      <button type="button" data-product="${item.id}" aria-pressed="${this.draft.includes(item.id)}">
        <img src="${item.image}" alt="" width="48" height="48">
        <span>${item.title}</span>
        <em>${this.#money(item.price)}</em>
      </button>`).join('') || '<p class="bundle-offer__empty">No watches match.</p>';
    this.querySelector('[data-count]').textContent = `${this.draft.length} of ${need}`;
    this.querySelector('[data-confirm]').disabled = this.draft.length !== need;
  }

  #render() {
    this.querySelectorAll('[data-tier]').forEach((button) => {
      button.setAttribute('aria-checked', button.dataset.tier === this.tier ? 'true' : 'false');
    });
    const slots = this.querySelector('[data-slots]');
    const need = this.#need();
    const current = this.#variant();
    const chosen = this.#chosen();
    slots.hidden = need === 0;
    if (need === 0) {
      slots.innerHTML = '';
    } else {
      const chips = chosen.map((product) => `
        <li>
          <img src="${product.image}" alt="" width="36" height="36">
          <span>${product.title}</span>
          <button type="button" data-clear="${product.id}" aria-label="Remove ${product.title}">Remove</button>
        </li>`).join('');
      const open = chosen.length < need
        ? `<button class="bundle-offer__open" type="button" data-open>${
            need === 1 ? 'Choose the second watch' : `Choose watches · ${chosen.length}/4`
          }</button>`
        : '';
      slots.innerHTML = `
        <div class="bundle-offer__this">
          <img src="${this.current.image}" alt="" width="44" height="44">
          <span><small>This watch</small><strong>${this.current.title}</strong></span>
          <em>${this.#money(current.price)}</em>
        </div>
        ${open}
        <ul class="bundle-offer__chosen">${chips}</ul>`;
    }
    const quote = this.#offerReady() ? this.#quote() : null;
    const total = this.querySelector('[data-total]');
    const pay = this.querySelector('[data-pay]');
    const compare = this.querySelector('[data-compare]');
    if (quote && total) {
      total.hidden = false;
      pay.hidden = false;
      pay.textContent = this.#money(quote.pay);
      compare.hidden = quote.compare <= quote.pay;
      compare.textContent = quote.compare > quote.pay ? this.#money(quote.compare) : '';
    } else if (total) {
      total.hidden = true;
      pay.textContent = '';
      compare.hidden = true;
      compare.textContent = '';
    }
    const note = this.querySelector('[data-note]');
    if (this.tier === 'single') {
      note.hidden = true;
      note.textContent = '';
    } else if (this.tier === 'duo') {
      note.hidden = false;
      note.textContent = `This watch, plus one you choose. ${this.rate}% off both.`;
    } else {
      note.hidden = false;
      note.textContent = 'This watch is included. Choose 4 more. You pay for the three highest prices.';
    }
    this.#label();
  }

  async #add() {
    const button = this.#cartButton();
    if (button) button.disabled = true;
    const items = [{ id: this.#variant().id, quantity: 1 }];
    this.#chosen().forEach((product) => items.push({ id: product.variantId, quantity: 1 }));
    const code = this.tier === 'set' ? 'B3G2' : 'DUO20';
    const sectionId = document.querySelector('#cart-drawer cart-items-component')?.dataset.sectionId;
    try {
      const added = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionId }),
      });
      if (!added.ok) throw new Error('add failed');
      const update = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code, sections: sectionId }),
      });
      const payload = update.ok ? await update.json() : await added.json();
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
      const sections = payload.sections || {};
      const event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      event.action = 'add';
      event.promise = Promise.resolve({
        cart: { totalQuantity: cart.item_count },
        detail: { didError: false, sections, itemCount: cart.item_count },
      });
      document.dispatchEvent(event);
      document.getElementById('cart-drawer')?.open();
      if (button) button.disabled = false;
      this.#label();
    } catch (error) {
      if (button) button.disabled = false;
      this.#label();
    }
  }
}

if (!customElements.get('bundle-offer')) {
  customElements.define('bundle-offer', BundleOffer);
}
